import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileMenuOpen]);
  
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  const isActive = (path) => {
    return location.pathname === path ? 'text-primary-400 font-medium' : 'text-gray-300 hover:text-primary-400';
  };
  
  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/projects', label: 'Projects' },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: 'Contact' }
  ];

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const mobileMenuVariants = {
    hidden: { 
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    visible: { 
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };
  
  return (
    <>
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            className="fixed inset-0 backdrop-blur-sm z-40 md:hidden"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={backdropVariants}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>
      
      <motion.nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'backdrop-blur-md shadow-sm' : ''
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 15 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
  <div className="flex items-center py-4">
    {/* Logo */}
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Link 
        to="/" 
        className="flex items-center text-2xl font-bold text-white font-heading hoverable"
      >
        TECHVEDA<span className="text-primary-500">.</span>
      </Link>
    </motion.div>

    {/* Nav Links - pushed to the right with ml-auto */}
    <div className="hidden md:flex items-center space-x-8 ml-auto">
      {navLinks.map((link) => (
        <motion.div
          key={link.path}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Link 
            to={link.path}
            className={`${isActive(link.path)} transition-colors duration-200 text-sm font-medium hoverable`}
          >
            {link.label}
          </Link>
        </motion.div>
      ))}
    </div>

    {/* Mobile Menu Button - stays right of logo in small screens */}
    <motion.button
      onClick={toggleMobileMenu}
      aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
      className="md:hidden flex items-center hoverable ml-auto z-50"
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
    >
      <div className="w-6 h-6 flex flex-col justify-between items-center relative">
        <motion.span 
          className="w-full h-0.5 bg-gray-100"
          animate={{
            rotate: mobileMenuOpen ? 45 : 0,
            y: mobileMenuOpen ? 10 : 0
          }}
          transition={{ duration: 0.3 }}
        />
        <motion.span 
          className="w-full h-0.5 bg-gray-100"
          animate={{
            opacity: mobileMenuOpen ? 0 : 1
          }}
          transition={{ duration: 0.3 }}
        />
        <motion.span 
          className="w-full h-0.5 bg-gray-100"
          animate={{
            rotate: mobileMenuOpen ? -45 : 0,
            y: mobileMenuOpen ? -10 : 0
          }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </motion.button>
  </div>
</div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              className="md:hidden overflow-hidden z-50 backdrop-blur-md"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={mobileMenuVariants}
            >
              <div className="px-4 pt-2 pb-6 space-y-6">
                <div className="grid grid-cols-1 gap-y-6">
                  {navLinks.map((link) => (
                    <motion.div
                      key={link.path}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link 
                        to={link.path}
                        className={`${isActive(link.path)} text-lg font-medium block text-center py-2 hoverable`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navbar; 